import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useDocReader, useDocReaderDispatch } from '../../store/docReaderContext';
import usePageHeights from '../../services/usePageHeights';
import './index.scss';
import { $document } from '@umu/umu-dom';
import throttle from 'lodash/throttle';
import useScrollingHideToolbar from '../../services/useScrollingHideToolbar';
import { RENDER_TYPE } from '../../base';
interface PdfListProps {
    children: React.ReactNode;
    isPdfOutline?: boolean;
    customControlVirtualList?: boolean;
}

let USE_VIRTUAL_LIST = true; // 虚拟列表开关
let disableScrollTimer: any = null;
const PdfList: React.FC<PdfListProps> = ({ children, isPdfOutline = false, customControlVirtualList = false }) => {
    const [state] = useDocReader();
    const dispatch = useDocReaderDispatch();
    const { pageNum, displayPageNum, isShowBar } = state;
    const listRef = useRef<HTMLDivElement | null>(null);
    const [visibleIndexes, setVisibleIndexes] = useState<number[]>([]);
    // 子元素高度数组
    const pageViewHeights = usePageHeights();
    const childrenRefs = useRef<HTMLDivElement[]>([]);
    // 滚动隐藏工具栏
    const { disable: disableHideScrollingToolbar, enable: enableHideScrollingToolbar } = useScrollingHideToolbar();
    useEffect(() => {
        disableHideScrollingToolbar();
        clearTimeout(disableScrollTimer);
        disableScrollTimer = setTimeout(() => {
            enableHideScrollingToolbar();
        }, 300);
    }, [isShowBar]);
    const pageBuffer = useRef<number[]>([]);
    /**
     * 滚动到指定页
     * @param index pageIndex
     * @returns
     */
    const scrollToPage = (index: number) => {
        disableHideScrollingToolbar();
        clearTimeout(disableScrollTimer);
        disableScrollTimer = setTimeout(() => {
            enableHideScrollingToolbar();
        }, 300);
        const targetDom = childrenRefs.current[index];
        if (targetDom) {
            targetDom.scrollIntoView({ block: 'center' });
            return;
        }
        const pageOffsetTop = pageViewHeights?.slice(0, index).reduce((a, b) => a + b, 0) * state.scale;
        const listContainerOffsetTop = listRef.current?.offsetTop || 0;
        const scrollTop = pageOffsetTop + listContainerOffsetTop;
        $document.documentElement.scrollTo({
            top: scrollTop,
        });
    };
    useEffect(() => {
        if (isPdfOutline) {
            return;
        }
        scrollToPage(pageNum.value - 1);
    }, [pageNum]);
    useEffect(() => {
        if (!isPdfOutline) {
            return;
        }
        scrollToPage(displayPageNum - 1);
    }, [displayPageNum]);

    // 列表滚动反向更新页码
    useEffect(() => {
        if (isPdfOutline) {
            return;
        }
        const pageNumUpdateObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const index = parseInt(entry.target.id);
                    if (entry.isIntersecting) {
                        dispatch({ type: 'updateDisplayPageNum', pageNum: index + 1 });
                    }
                });
            },
            {
                root: null,
                rootMargin: '-49% -1% -49% -1%',
                threshold: 0.01,
            },
        );
        childrenRefs.current.forEach((ref) => {
            if (ref) {
                pageNumUpdateObserver.observe(ref);
            }
        });
        return () => {
            pageNumUpdateObserver.disconnect();
        };
    }, []);

    // 虚拟列表监听器
    const updateVisibleIndexes = throttle(() => {
        // 最多渲染10页
        setVisibleIndexes([...pageBuffer.current].slice(0, 10));
    }, 300);
    useEffect(() => {
        if (state.renderType === RENDER_TYPE.IMAGE) {
            USE_VIRTUAL_LIST = false;
        }
        if (!USE_VIRTUAL_LIST) {
            return;
        }
        // 上下两屏内的所有元素都会被渲染
        const pageBufferObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const index = parseInt(entry.target.id);
                    if (entry.isIntersecting) {
                        pageBuffer.current.push(index);
                    } else {
                        const indexToRemove = pageBuffer.current.indexOf(index);
                        if (indexToRemove !== -1) {
                            pageBuffer.current.splice(indexToRemove, 1);
                        }
                    }
                });
                pageBuffer.current.sort((a, b) => a - b);
                updateVisibleIndexes();
            },
            {
                root: null,
                rootMargin: '100% -1% 100% -1%', // 上下两屏
                threshold: 0.01,
            },
        );

        childrenRefs.current.forEach((ref) => {
            if (ref) {
                pageBufferObserver.observe(ref);
            }
        });
        return () => {
            pageBufferObserver.disconnect();
            updateVisibleIndexes.cancel();
        };
    }, []);

    return (
        <div ref={listRef} className="micro-page-pdf-list list">
            {React.Children.map(children, (child, index) => {
                return (
                    <div
                        id={index.toString()}
                        style={{
                            height: `calc(var(--scale-factor) * ${pageViewHeights?.[index]}px)`,
                        }}
                        className={classNames({
                            'list-item': true,
                            'micro-page-pdf-list-item': true,
                            'list-item-active': pageNum.value === index + 1,
                            'list-item-prev': index + 1 < pageNum.value,
                            'list-item-next': index + 1 > pageNum.value,
                        })}
                        ref={(ref) => {
                            if (!ref) {
                                return;
                            }
                            childrenRefs.current[index] = ref;
                        }}
                    >
                        {/* 如果 customControlVirtualList 为true, 则将是否渲染作为参数传给child */}
                        {(() => {
                            if (!USE_VIRTUAL_LIST) {
                                return child;
                            }
                            if (!customControlVirtualList) {
                                if (visibleIndexes.includes(index)) {
                                    return child;
                                }
                            } else if (visibleIndexes.includes(index)) {
                                return React.cloneElement(child as React.ReactElement, {
                                    visible: visibleIndexes.includes(index),
                                });
                            }
                            return <></>;
                        })()}
                    </div>
                );
            })}
        </div>
    );
};
export default PdfList;
